import type { AssetKind } from "@cva/shared";
import { create } from "zustand";

/**
 * State of the editor's left side: which rail tab is open, the view inside
 * the combined tabs and the media filter. A store instead of component state so
 * other parts (timeline, canvas) can open a tab, and so a tab keeps its view
 * when the user switches away and back.
 */

export type LeftTab = "media" | "elements" | "blocks" | "brand" | "pages" | "layers";

/** Views of the combined „Bausteine“ tab. */
export type BlocksView = "blocks" | "layouts";

/** Filter of the media library („all“ = every kind the editor can use). */
export type MediaFilter = "all" | AssetKind;

export type EditorKind = "design" | "video";

export const LEFT_TABS: Record<EditorKind, LeftTab[]> = {
  design: ["elements", "blocks", "media", "brand", "pages", "layers"],
  video: ["media", "elements", "blocks", "brand", "layers"],
};

const DEFAULT_TAB: Record<EditorKind, LeftTab> = { design: "elements", video: "media" };

const STORAGE_KEY = "cva.editor.leftNav";

interface Saved {
  tab: LeftTab;
  open: boolean;
}

function loadSaved(kind: EditorKind): Saved {
  const fallback = { tab: DEFAULT_TAB[kind], open: true };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const entry = raw
      ? (JSON.parse(raw) as Partial<Record<EditorKind, Partial<Saved>>>)[kind]
      : null;
    if (!entry) return fallback;
    const tab = LEFT_TABS[kind].includes(entry.tab as LeftTab)
      ? (entry.tab as LeftTab)
      : fallback.tab;
    return { tab, open: entry.open !== false };
  } catch {
    return fallback;
  }
}

function save(kind: EditorKind, saved: Saved) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const all = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...all, [kind]: saved }));
  } catch {
    // Private mode or blocked storage: the layout lasts until the tab closes.
  }
}

interface LeftNavState {
  kind: EditorKind;
  /** Open tab, null = panel collapsed. */
  tab: LeftTab | null;
  /** Tab shown when the collapsed panel opens again. */
  lastTab: LeftTab;
  blocksView: BlocksView;
  mediaFilter: MediaFilter;
  /** Category opened in the elements tab (null = overview). */
  elementsCategory: string | null;
  /** New editor: restores the last layout of this editor kind. */
  reset(kind: EditorKind): void;
  open(tab: LeftTab, view?: { blocks?: BlocksView; media?: MediaFilter }): void;
  /** Rail click: opens the tab, a second click collapses the panel. */
  toggle(tab: LeftTab): void;
  collapse(): void;
  expand(): void;
  setBlocksView(view: BlocksView): void;
  setMediaFilter(filter: MediaFilter): void;
  setElementsCategory(category: string | null): void;
}

export const useLeftNav = create<LeftNavState>()((set, get) => {
  const persist = () => {
    const { kind, tab, lastTab } = get();
    save(kind, { tab: tab ?? lastTab, open: tab !== null });
  };
  return {
    kind: "design",
    tab: DEFAULT_TAB.design,
    lastTab: DEFAULT_TAB.design,
    blocksView: "blocks",
    mediaFilter: "all",
    elementsCategory: null,
    reset(kind) {
      const saved = loadSaved(kind);
      set({
        kind,
        tab: saved.open ? saved.tab : null,
        lastTab: saved.tab,
        blocksView: "blocks",
        mediaFilter: "all",
        elementsCategory: null,
      });
    },
    open(tab, view) {
      set({
        tab,
        lastTab: tab,
        ...(view?.blocks ? { blocksView: view.blocks } : {}),
        ...(view?.media ? { mediaFilter: view.media } : {}),
      });
      persist();
    },
    toggle(tab) {
      if (get().tab === tab) get().collapse();
      else get().open(tab);
    },
    collapse() {
      set({ tab: null });
      persist();
    },
    expand() {
      get().open(get().lastTab);
    },
    setBlocksView(view) {
      set({ blocksView: view });
    },
    setMediaFilter(filter) {
      set({ mediaFilter: filter });
    },
    setElementsCategory(category) {
      set({ elementsCategory: category });
    },
  };
});
